/* Nelson Made header behaviors:
   - <nm-header>: tracks scroll for the transparent -> solid transition and
     publishes the header's bottom edge as --nm-header-bottom (used by the
     mobile drawer to position itself under the bar).
   - <nm-menu-drawer>: full-screen mobile menu on details/summary with body
     scroll lock, Escape to close, and focus trapping via Dawn's trapFocus. */

class NMHeader extends HTMLElement {
  connectedCallback() {
    this.onScrollHandler = this.update.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    window.addEventListener('resize', this.onScrollHandler);
    this.update();
    this.initHoverMenus();
  }

  /* Desktop mega menus open on hover (real hover devices only — touch and
     keyboard keep the click/disclosure behavior from details-disclosure.js). */
  initHoverMenus() {
    const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.querySelectorAll('header-menu').forEach((menu) => {
      const details = menu.querySelector('details');
      const summary = menu.querySelector('summary');
      if (!details || !summary) return;
      let closeTimer;

      menu.addEventListener('mouseenter', () => {
        if (!hoverQuery.matches) return;
        clearTimeout(closeTimer);
        if (!details.open) {
          details.setAttribute('open', '');
          summary.setAttribute('aria-expanded', 'true');
        }
      });

      menu.addEventListener('mouseleave', () => {
        if (!hoverQuery.matches) return;
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => {
          if (details.open && !menu.contains(document.activeElement)) {
            details.removeAttribute('open');
            summary.setAttribute('aria-expanded', 'false');
          }
        }, 150);
      });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
    window.removeEventListener('resize', this.onScrollHandler);
  }

  update() {
    this.classList.toggle('nm-header--solid', window.scrollY > 24);
    const bottom = Math.max(this.getBoundingClientRect().bottom, 0);
    this.style.setProperty('--nm-header-bottom', `${bottom}px`);
  }
}

customElements.define('nm-header', NMHeader);

class NMMenuDrawer extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.drawer = this.querySelector('.nm-drawer');
    if (!this.details || !this.summary || !this.drawer) return;

    this.details.addEventListener('toggle', this.onToggle.bind(this));
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() !== 'ESCAPE' || !this.details.open) return;
      // Let an open search modal inside the drawer consume Escape first.
      if (event.target.closest('details-modal details[open]')) return;
      this.close();
    });
  }

  onToggle() {
    const header = this.closest('nm-header');
    if (header) header.update();
    document.body.classList.toggle('nm-drawer-open', this.details.open);
    this.summary.setAttribute('aria-expanded', this.details.open);

    if (this.details.open) {
      if (typeof trapFocus === 'function') trapFocus(this.drawer, this.drawer);
    } else if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.summary);
    }
  }

  close() {
    this.details.removeAttribute('open');
  }
}

customElements.define('nm-menu-drawer', NMMenuDrawer);
